// LeadsUnified.jsx — 统一线索管理页（合并 institution_applications + geolook_leads，按来源分类）
import { useState, useEffect } from 'react'
import { Table, Tag, Card, Select, Button, Modal, Input, Space, Divider, message } from 'antd'
import { CheckOutlined, CloseOutlined, EyeOutlined, UserAddOutlined } from '@ant-design/icons'

const { TextArea } = Input

const SOURCE_TABS = [
  { value: 'all', label: '全部线索' },
  { value: 'landing', label: '🌐 品牌落地页' },
  { value: 'brand-doctor', label: '🏷️ 品牌诊断' },
  { value: 'skill-market', label: '🧩 AI广场 Skill 下载' }
]

const sourceLabel = (s) => {
  const t = SOURCE_TABS.find(t => t.value === s)
  return t ? t.label : (s || '-')
}

const STATUS_MAP = {
  pending: { label: '待处理', color: 'orange' },
  approved: { label: '已通过', color: 'green' },
  rejected: { label: '已驳回', color: 'red' },
  contacted: { label: '已联系', color: 'blue' },
  completed: { label: '已完成', color: 'green' },
  invalid: { label: '无效', color: 'default' }
}

const TYPE_LABELS = {
  parent: '👶 家长', kindergarten: '🏫 机构', studybase: '🏪 基地', other: '📋 其他'
}

export default function LeadsUnified() {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [sourceFilter, setSourceFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const loadLeads = async () => {
    setLoading(true)
    try {
      // 并行拉两个数据源
      const [inquiriesRes, geolookRes] = await Promise.all([
        fetch('/api/inquiries?page=1&limit=500').then(r => r.json()).catch(() => ({ success: false, data: [] })),
        fetch('/api/geolook-leads?page=1&limit=500').then(r => r.json()).catch(() => ({ success: false, data: [] }))
      ])

      const iq = (inquiriesRes.success ? inquiriesRes.data : []).map(r => ({
        key: 'iq_' + r.id,
        id: r.id,
        source: r.source || 'landing',
        name: r.name || r.contact_name || '-',
        phone: r.phone || '-',
        type: r.type,
        typeLabel: TYPE_LABELS[r.type] || r.type || '-',
        industry: r.industry || '',
        remark: r.description || '',
        status: r.status || 'pending',
        created_at: r.created_at,
        kind: 'inquiry',
        raw: r
      }))

      const gl = (geolookRes.success ? geolookRes.data : []).map(r => ({
        key: 'gl_' + r.id,
        id: r.id,
        source: r.source || 'brand-doctor',
        name: r.brand_name || r.contact_name || '-',
        phone: r.phone || '-',
        type: '',
        typeLabel: r.industry || '-',
        industry: r.industry || '',
        remark: r.remark || '',
        status: r.status || 'pending',
        created_at: r.created_at,
        kind: 'geolook',
        raw: r
      }))

      let merged = [...iq, ...gl]
      if (sourceFilter !== 'all') merged = merged.filter(l => l.source === sourceFilter)
      if (statusFilter !== 'all') merged = merged.filter(l => l.status === statusFilter)
      merged.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))

      setLeads(merged)
      setTotal(merged.length)
    } catch (e) {
      message.error('加载失败: ' + e.message)
    }
    setLoading(false)
  }

  useEffect(() => { loadLeads() }, [sourceFilter, statusFilter])

  const openDetail = (lead) => {
    setSelected(lead)
    setDetailOpen(true)
    setRejectReason(lead.raw.reject_reason || '')
  }

  // 机构申请审核（通过/驳回）
  const handleApprove = async () => {
    setActionLoading(true)
    try {
      const res = await fetch(`/api/inquiries/${selected.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve' })
      })
      const json = await res.json()
      if (json.success) { message.success('已通过！'); setDetailOpen(false); loadLeads() }
      else message.error(json.message || '操作失败')
    } catch (e) { message.error('网络错误') }
    setActionLoading(false)
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) { message.warning('请填写驳回原因'); return }
    setActionLoading(true)
    try {
      const res = await fetch(`/api/inquiries/${selected.id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reject', reject_reason: rejectReason })
      })
      const json = await res.json()
      if (json.success) { message.success('已驳回'); setDetailOpen(false); loadLeads() }
      else message.error(json.message || '操作失败')
    } catch (e) { message.error('网络错误') }
    setActionLoading(false)
  }

  // 品牌/技能线索状态更新
  const handleGeolookStatus = async (record, status) => {
    try {
      const res = await fetch(`/api/geolook-leads/${record.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      })
      const json = await res.json()
      if (json.success) { message.success('状态已更新'); loadLeads() }
      else message.error(json.message || '更新失败')
    } catch (e) { message.error('网络错误') }
  }

  // 线索转正式客户（企业大脑客户档案）：建 customers + 首条跟进 + 线索标已联系
  const handleConvert = async (lead) => {
    if (!lead) return
    setActionLoading(true)
    try {
      const srcLabel = sourceLabel(lead.source)
      const payload = {
        name: lead.name,
        contact: lead.name,
        phone: lead.phone === '-' ? '' : lead.phone,
        source: lead.source === 'skill-market' ? 'brain-kit' : '品牌诊断',
        tags: [srcLabel, lead.industry || (lead.remark || '').split('|')[0] || ''].filter(Boolean),
        notes: lead.remark || ''
      }
      const res = await fetch('/cms/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const json = await res.json()
      if (!json.success) { message.error(json.error || '转化失败'); setActionLoading(false); return }
      // 首条跟进记录
      try {
        await fetch(`/cms/api/customers/${json.data.id}/followup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: `来自${srcLabel}线索转化 | 原始备注: ${lead.remark || '-'}`,
            type: '线索转化'
          })
        })
      } catch (e) { /* 跟进记录失败不阻塞转化 */ }
      // 线索状态标已联系
      if (lead.kind === 'geolook') {
        try {
          await fetch(`/api/geolook-leads/${lead.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'contacted' })
          })
        } catch (e) { /* 忽略 */ }
      }
      message.success(`已转为正式客户（ID: ${json.data.id}），去"客户管理"看档案`)
      setDetailOpen(false)
      loadLeads()
    } catch (e) { message.error('网络错误') }
    setActionLoading(false)
  }

  const columns = [
    {
      title: '来源', dataIndex: 'source', key: 'source', width: 150,
      render: (s) => {
        if (s === 'skill-market') return <Tag color="purple">🧩 AI广场</Tag>
        if (s === 'brand-doctor') return <Tag color="blue">🏷️ 品牌诊断</Tag>
        return <Tag color="green">🌐 落地页</Tag>
      }
    },
    { title: '名称', dataIndex: 'name', key: 'name', width: 120 },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
    {
      title: '行业/身份', dataIndex: 'typeLabel', key: 'typeLabel', width: 110,
      render: (t) => t || '-'
    },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true,
      render: (r) => {
        if (!r) return '-'
        const parts = r.split('|')
        return parts[0].replace('感兴趣产品: ', '')
      }
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 130,
      render: (s, record) => {
        const m = STATUS_MAP[s] || { label: s, color: 'default' }
        if (record.kind === 'geolook') {
          return (
            <Select
              size="small"
              value={s}
              style={{ width: 90 }}
              onChange={(v) => handleGeolookStatus(record, v)}
              options={['pending', 'contacted', 'completed', 'invalid'].map(v => ({
                value: v, label: STATUS_MAP[v].label
              }))}
            />
          )
        }
        return <Tag color={m.color}>{m.label}</Tag>
      }
    },
    {
      title: '时间', dataIndex: 'created_at', key: 'created_at', width: 150,
      render: (t) => t ? new Date(t).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'
    },
    {
      title: '操作', key: 'action', width: 130,
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)}>查看</Button>
          {record.kind === 'geolook' && (
            <Button type="link" size="small" icon={<UserAddOutlined />} loading={actionLoading} onClick={() => handleConvert(record)}>转客户</Button>
          )}
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>📋 线索管理（全渠道）</span>
            <Select
              value={sourceFilter}
              onChange={(v) => { setSourceFilter(v); setStatusFilter('all') }}
              style={{ width: 170 }}
              size="small"
              options={SOURCE_TABS}
            />
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              style={{ width: 110 }}
              size="small"
              options={[
                { value: 'all', label: '全部状态' },
                { value: 'pending', label: '待处理' },
                { value: 'contacted', label: '已联系' },
                { value: 'completed', label: '已完成' },
                { value: 'approved', label: '已通过' },
                { value: 'rejected', label: '已驳回' },
                { value: 'invalid', label: '无效' }
              ]}
            />
          </div>
        }
        extra={<span style={{ color: '#888', fontSize: 13 }}>共 {total} 条线索</span>}
      >
        <Table
          dataSource={leads}
          columns={columns}
          rowKey="key"
          loading={loading}
          size="middle"
          pagination={{ pageSize: 50, showTotal: (t) => `共 ${t} 条线索` }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="线索详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={
          selected?.kind === 'inquiry' && selected?.status === 'pending' ? [
            <Button key="reject" danger loading={actionLoading} icon={<CloseOutlined />} onClick={handleReject}>驳回</Button>,
            <Button key="approve" type="primary" loading={actionLoading} icon={<CheckOutlined />} onClick={handleApprove}>通过</Button>
          ] : selected?.kind === 'geolook' ? [
            <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>,
            <Button key="convert" type="primary" loading={actionLoading} icon={<UserAddOutlined />} onClick={() => handleConvert(selected)}>转为正式客户</Button>
          ] : [
            <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>
          ]
        }
      >
        {selected && (
          <div>
            <p><strong>来源：</strong>{sourceLabel(selected.source)}</p>
            <p><strong>名称：</strong>{selected.name}</p>
            <p><strong>手机号：</strong>{selected.phone}</p>
            {selected.kind === 'inquiry' ? (
              <>
                <p><strong>身份：</strong>{selected.typeLabel}</p>
                <p><strong>时间：</strong>{selected.created_at ? new Date(selected.created_at).toLocaleString('zh-CN') : '-'}</p>
                <Divider />
                <p><strong>感兴趣产品：</strong>{
                  selected.remark ? (selected.remark.includes('|') ? selected.remark.split('|')[0].replace('感兴趣产品: ', '') : selected.remark) : '-'
                }</p>
                <p><strong>留言：</strong>{
                  selected.remark && selected.remark.includes('|') ? (selected.remark.split('|')[1] || '-') : '-'
                }</p>
              </>
            ) : (
              <>
                <p><strong>行业：</strong>{selected.industry || '-'}</p>
                <p><strong>时间：</strong>{selected.created_at ? new Date(selected.created_at).toLocaleString('zh-CN') : '-'}</p>
                <Divider />
                <p><strong>备注：</strong>{selected.remark || '-'}</p>
              </>
            )}
            {selected.status === 'rejected' && selected.raw.reject_reason && (
              <p style={{ color: '#f5222d' }}><strong>驳回原因：</strong>{selected.raw.reject_reason}</p>
            )}
          </div>
        )}
        {selected?.kind === 'inquiry' && selected?.status === 'pending' && (
          <div style={{ marginTop: 16 }}>
            <p><strong>驳回原因：</strong></p>
            <TextArea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="填写驳回原因..." rows={2} />
          </div>
        )}
      </Modal>
    </div>
  )
}
